import { AnalysisResult, CompatibilityIssue } from "../analyze/models.js";
import { CiReport } from "../models/ciReport.js";
import { ThresholdEvaluation } from "./ciThresholdEvaluator.js";

const SEVERITY_ORDER: Record<CompatibilityIssue["severity"], number> = {
  error: 0,
  warning: 1,
  info: 2,
};

/**
 * Sorts issues deterministically for stable CI output:
 *  1. Severity descending (error first)
 *  2. packageName ascending
 *  3. message ascending
 *
 * This ensures GitHub Actions annotation ordering is stable
 * across reruns regardless of plugin evaluation order.
 */
export function sortIssues(issues: CompatibilityIssue[]): CompatibilityIssue[] {
  return [...issues].sort((a, b) => {
    const severityDiff = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
    if (severityDiff !== 0) return severityDiff;

    const pkgDiff = a.packageName.localeCompare(b.packageName);
    if (pkgDiff !== 0) return pkgDiff;

    return a.message.localeCompare(b.message);
  });
}

/**
 * Assembles a CiReport from an AnalysisResult and a threshold evaluation.
 *
 * `generatedAt` is passed in as a parameter so callers can control the
 * timestamp (useful for deterministic tests).
 */
export function generateCiReport(
  result: AnalysisResult,
  evaluation: ThresholdEvaluation,
  generatedAt: string = new Date().toISOString()
): CiReport {
  const allIssues: CompatibilityIssue[] = [
    ...result.errors,
    ...result.warnings,
    ...result.info,
  ];

  const sorted = sortIssues(allIssues);

  return {
    status: evaluation.outcome === "PASS" ? "pass" : "fail",
    score: result.score,
    errors: result.errors.length,
    warnings: result.warnings.length,
    infos: result.info.length,
    issueCount: allIssues.length,
    issues: sorted,
    generatedAt,
  };
}
